import { AutomatedContentBanner } from "@2u-landing-pages/components/AutomatedContent";
import { Accordion } from "@2u-landing-pages/templates/Accordion";
import { Banner } from "@2u-landing-pages/templates/Banner";
import { Bento } from "@2u-landing-pages/templates/Bento";
import { Carousel } from "@2u-landing-pages/templates/Carousel";
import { DynamicExperienceFlow } from "@2u-landing-pages/templates/DynamicExperienceFlow";
import { Flex } from "@2u-landing-pages/templates/Flex";
import { Form } from "@2u-landing-pages/templates/Form";
import { Gallery } from "@2u-landing-pages/templates/Gallery";
import { Hero } from "@2u-landing-pages/templates/Hero";
import { Split } from "@2u-landing-pages/templates/Split";
import { Tabs } from "@2u-landing-pages/templates/Tabs";
import {
  isTypeBanner,
  TypeAccordionWithoutUnresolvableLinksResponse,
  TypeDynamic_experience_flowWithoutUnresolvableLinksResponse,
  TypeAutomated_contentWithoutUnresolvableLinksResponse,
  TypeBannerWithoutUnresolvableLinksResponse,
  TypeBentoWithoutUnresolvableLinksResponse,
  TypeCarouselWithoutUnresolvableLinksResponse,
  TypeFlexWithoutUnresolvableLinksResponse,
  TypeFormWithoutUnresolvableLinksResponse,
  TypeGalleryWithoutUnresolvableLinksResponse,
  TypeHeroWithoutUnresolvableLinksResponse,
  TypeProgram_variationWithoutUnresolvableLinksResponse,
  TypeProgramWithoutUnresolvableLinksResponse,
  TypeSchoolWithoutUnresolvableLinksResponse,
  TypeSplitWithoutUnresolvableLinksResponse,
  TypeSticky_elementWithoutUnresolvableLinksResponse,
  TypeTabsWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import EventClient from "@2u-landing-pages/utils/event-client";
import { useIntersectionObserver } from "@2u-landing-pages/utils/useIntersectionObserver";
import { useContentfulInspectorMode } from "@contentful/live-preview/react";
import React, { useCallback, useEffect, useRef, useState } from "react";

type Section =
  | TypeHeroWithoutUnresolvableLinksResponse
  | TypeBannerWithoutUnresolvableLinksResponse
  | TypeAccordionWithoutUnresolvableLinksResponse
  | TypeBentoWithoutUnresolvableLinksResponse
  | TypeCarouselWithoutUnresolvableLinksResponse
  | TypeFormWithoutUnresolvableLinksResponse
  | TypeSplitWithoutUnresolvableLinksResponse
  | TypeGalleryWithoutUnresolvableLinksResponse
  | TypeSticky_elementWithoutUnresolvableLinksResponse
  | TypeFlexWithoutUnresolvableLinksResponse
  | TypeTabsWithoutUnresolvableLinksResponse
  | TypeAutomated_contentWithoutUnresolvableLinksResponse
  | TypeDynamic_experience_flowWithoutUnresolvableLinksResponse;

interface ComponentRendererProps {
  section: Section;
  school: TypeSchoolWithoutUnresolvableLinksResponse;
  sectionName: string;
  phoneNumber: string;
  programLogo: TypeProgram_variationWithoutUnresolvableLinksResponse["fields"]["logo_primary"];
  inspector: (props: { entryId: string; fieldId: string }) => any;
  isLoading: boolean;
  mode: "multi-program" | "single-program";
}

const ComponentRenderer: React.FC<ComponentRendererProps> = ({ section, ...rest }) => {
  const id = section?.sys?.id;

  switch (section?.sys?.contentType?.sys?.id) {
    case "hero":
      return <Hero id={id} {...(section as TypeHeroWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "banner":
      return <Banner id={id} {...(section as TypeBannerWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "automated_content":
      return (
        <AutomatedContentBanner
          id={id}
          {...(section as TypeAutomated_contentWithoutUnresolvableLinksResponse).fields}
          {...rest}
        />
      );
    case "accordion":
      return <Accordion id={id} {...(section as TypeAccordionWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "bento":
      return <Bento id={id} {...(section as TypeBentoWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "carousel":
      return <Carousel id={id} {...(section as TypeCarouselWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "form":
      return <Form id={id} {...(section as TypeFormWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "split":
      return <Split id={id} {...(section as TypeSplitWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "flex":
      return <Flex id={id} {...(section as TypeFlexWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "tabs":
      return <Tabs id={id} {...(section as TypeTabsWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "gallery":
      return <Gallery id={id} {...(section as TypeGalleryWithoutUnresolvableLinksResponse).fields} {...rest} />;
    case "dynamic_experience_flow":
      return <DynamicExperienceFlow {...(section as TypeDynamic_experience_flowWithoutUnresolvableLinksResponse).fields} {...rest} />;
    default:
      return null;
  }
};

interface SectionsProps {
  sections: TypeProgram_variationWithoutUnresolvableLinksResponse["fields"]["sections"];
  program: TypeProgramWithoutUnresolvableLinksResponse;
  school: TypeSchoolWithoutUnresolvableLinksResponse;
  programLogo: TypeProgram_variationWithoutUnresolvableLinksResponse["fields"]["logo_primary"];
  phoneNumber: string;
  isLoading: boolean;
}

export const Sections: React.FC<SectionsProps> = ({
  phoneNumber,
  sections,
  school,
  program,
  programLogo,
  isLoading,
}) => {
  const inspector = useContentfulInspectorMode();

  const [bannerHeight, setBannerHeight] = useState(0);
  const staticBannerRef = useRef<HTMLDivElement | null>(null);

  const updateBannerHeight = useCallback(() => {
    if (staticBannerRef.current) {
      setBannerHeight(staticBannerRef.current.offsetHeight);
    } else {
      setBannerHeight(0);
    }
  }, []);

  useEffect(() => {
    if (!staticBannerRef.current) return;
    const observer = new window.ResizeObserver(updateBannerHeight);
    observer.observe(staticBannerRef.current);
    return () => {
      observer.disconnect();
    };
  }, [updateBannerHeight]);

  const handleIntersect = useCallback((target: HTMLElement) => {
    const { position, name } = target.dataset;

    EventClient.elementViewed({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: `Section ${position}`,
        text: name,
      },
    });
  }, []);

  const addObserver = useIntersectionObserver(handleIntersect, { threshold: 0.5 });

  return (
    <>
      {sections?.map((section, index) => {
        if (!section) return null;

        if (!section?.sys?.contentType?.sys?.id) return null;

        const style = {} as React.CSSProperties;
        if (isTypeBanner(section) && section?.fields?.sticky) {
          style.position = "sticky";
          style.top = bannerHeight ? `${bannerHeight}px` : 0;
          style.zIndex = 100;
        }

        return (
          <div
            key={section.sys.id}
            id={section.fields.section_id || section.sys.id}
            data-name={section.fields.name}
            data-position={index + 1}
            ref={(element) => {
              if (element) addObserver(element);
            }}
            style={style}
          >
            <ComponentRenderer
              section={section as Section}
              school={school}
              sectionName={section.fields.name || `Section ${index + 1}`}
              phoneNumber={phoneNumber}
              programLogo={programLogo}
              inspector={inspector}
              isLoading={isLoading}
              mode="single-program"
            />
          </div>
        );
      })}
    </>
  );
};
